// ─────────────────────────────────────────────────────────────────────────────
// Visit Leaderboard — Excel export (added 2026-09-11)
//
// Same conventions as exportPlatformSummaryExcel.ts in this repo: metadata header rows, a
// styled header row, a formula-injection guard on any text cell, and
// sensible column widths.
// ─────────────────────────────────────────────────────────────────────────────

import { createExcelWorkbook, downloadExcelWorkbook, setExcelColumnWidths } from './excelExport';
import { LeaderboardEntry } from '../services/visitTrackingService';

const EXCEL_INJECTION_PREFIX_PATTERN = /^\s*[=+\-@]/;

const HEADER_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF2F4F5' } };
const THIN_BORDER_COLOR = 'D0D7DE';
const DATA_BORDER_COLOR = 'E1E5E9';

function thinBorder(color: string) {
  return {
    top: { style: 'thin' as const, color: { argb: `FF${color}` } },
    left: { style: 'thin' as const, color: { argb: `FF${color}` } },
    bottom: { style: 'thin' as const, color: { argb: `FF${color}` } },
    right: { style: 'thin' as const, color: { argb: `FF${color}` } },
  };
}

function sanitizeExcelText(value: string): string {
  if (!value) return value;
  return EXCEL_INJECTION_PREFIX_PATTERN.test(value) ? `'${value}` : value;
}

function formatExportTimestamp(date: Date): string {
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}-${pad(date.getMinutes())}`;
}

function formatHumanTimestamp(date: Date): string {
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatDurationForExport(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds <= 0) return '0m';
  const minutes = Math.round(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes > 0 ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

export interface VisitLeaderboardExportSnapshot {
  generatedAt: Date;
  ranged: boolean;
  dateFrom: string | null; // YYYY-MM-DD, only when ranged
  dateTo: string | null;
  leaders: LeaderboardEntry[];
}

export async function exportVisitLeaderboardToExcel(snapshot: VisitLeaderboardExportSnapshot): Promise<{ fileName: string; rowCount: number }> {
  const wb = await createExcelWorkbook();
  const aoa: Array<Array<string | number | null>> = [];

  aoa.push(['Report', 'Automation Insights — Visit Leaderboard']);
  aoa.push(['Export Generated', formatHumanTimestamp(snapshot.generatedAt)]);
  aoa.push([
    'Date Range',
    snapshot.ranged && snapshot.dateFrom && snapshot.dateTo ? `${snapshot.dateFrom} to ${snapshot.dateTo}` : 'All time',
  ]);
  aoa.push([]);

  const headerRowIndex = aoa.length;
  const headers = ['#', 'User', 'Email', 'Visits', 'Avg time (min)', 'Avg time', 'Last active'];
  aoa.push(headers);

  const dataStartRowIndex = aoa.length;
  snapshot.leaders.forEach((entry, index) => {
    aoa.push([
      index + 1,
      sanitizeExcelText(entry.user_name),
      entry.user_email ? sanitizeExcelText(entry.user_email) : '',
      entry.visit_count,
      Math.round(entry.avg_duration_seconds / 60),
      formatDurationForExport(entry.avg_duration_seconds),
      entry.last_visit_at ? sanitizeExcelText(entry.last_visit_at) : '',
    ]);
  });
  const dataEndRowIndex = aoa.length - 1;

  const ws = wb.addWorksheet('Visit Leaderboard');
  ws.addRows(aoa);

  for (let c = 0; c < headers.length; c += 1) {
    const cell = ws.getCell(headerRowIndex + 1, c + 1);
    if (cell.value === null) continue;
    cell.style = {
      font: { bold: true },
      alignment: { vertical: 'middle', horizontal: 'left' },
      fill: HEADER_FILL,
      border: thinBorder(THIN_BORDER_COLOR),
    };
  }

  for (let r = dataStartRowIndex; r <= dataEndRowIndex; r += 1) {
    for (let c = 0; c < headers.length; c += 1) {
      const cell = ws.getCell(r + 1, c + 1);
      if (cell.value === null) continue;
      const isNumericCell = c === 0 || c === 3 || c === 4;
      cell.style = {
        alignment: { vertical: 'middle', horizontal: isNumericCell ? 'right' : 'left' },
        border: thinBorder(DATA_BORDER_COLOR),
      };
    }
  }

  setExcelColumnWidths(ws, [5, 24, 28, 10, 15, 12, 22]);

  const rangeSuffix = snapshot.ranged && snapshot.dateFrom && snapshot.dateTo ? `${snapshot.dateFrom}_to_${snapshot.dateTo}` : 'AllTime';
  const fileName = `VisitLeaderboard_${rangeSuffix}_${formatExportTimestamp(snapshot.generatedAt)}.xlsx`;
  await downloadExcelWorkbook(wb, fileName);

  return { fileName, rowCount: snapshot.leaders.length };
}
